import React, { useState } from 'react';
import type { Change, UserVoteInfo } from '../types';
import VoteButton from './VoteButton';
import VoterList from './VoterList';

const statusColors: Record<number, string> = {
  // early
  0: 'bg-blue-100 text-blue-700',
  149: 'bg-blue-100 text-blue-700',
  // preparation
  150: 'bg-emerald-100 text-emerald-700',
  179: 'bg-emerald-100 text-emerald-700',
  // implementation
  180: 'bg-orange-100 text-orange-700',
  229: 'bg-orange-100 text-orange-700',
  // closed
  230: 'bg-red-100 text-red-600',
  512: 'bg-red-100 text-red-600',
};

interface TicketCardProps {
  ticket: Change;
  userVoteInfo: UserVoteInfo | null;
  onVoteChange: (changeKey: string, action: string) => Promise<void>;
}

const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  userVoteInfo,
  onVoteChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasDescription = ticket.description && ticket.description.trim().length > 0;
  const shortDescription = ticket.description.slice(0, 200);
  const needsReadMore = ticket.description.length > 200;

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Onbekend';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasUserVoted =
    userVoteInfo && userVoteInfo.email
      ? ticket.votes?.some(
          (vote) =>
            String(vote.userEmail || '').toLowerCase() ===
            String(userVoteInfo.email).toLowerCase()
        )
      : false;

  const getClosestColor = (number: number): string => {
    const keys = Object.keys(statusColors).map(Number);
    let closest = keys[0];
    let minDiff = Math.abs(number - closest);

    for (let i = 1; i < keys.length; i++) {
      const diff = Math.abs(number - keys[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closest = keys[i];
      }
    }

    return statusColors[closest] || 'bg-neutral-100 text-neutral-600';
  };

  return (
    <div
      className={`bg-neutral-50 border ${ticket.isVotingOpen ? 'border-primary-600' : 'border-neutral-200'} rounded-lg p-6 ${ticket.isVotingOpen ? 'hover:shadow-lg transition-shadow duration-200' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-block bg-primary-100 text-primary-600 text-sm font-semibold px-3 py-1 rounded">
            {ticket.key}
          </span>
          <span
            className={`inline-block text-xs font-medium px-2.5 py-1 rounded ${getClosestColor(ticket.status)}`}
          >
            {ticket.statusName || 'Onbekend'}
          </span>
          {hasUserVoted && (
            <span className="inline-block bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded flex items-center gap-1">
              <i className="fa-light fa-check" />
              Gestemd
            </span>
          )}
        </div>
      </div>

      <h3 className="text-lg font-semibold text-primary-600 mb-3 break-words">
        {ticket.summary}
      </h3>
      <div className="mb-3 flex flex-wrap gap-4 text-xs text-neutral-600">
        <div className="flex items-center gap-1.5">
          <i className="fa-light fa-calendar" />
          <span className="font-medium">Aangemaakt:</span>
          <span>{formatDate(ticket.created)}</span>
        </div>
      </div>

      {hasDescription && (
        <div className="text-neutral-700 text-sm leading-relaxed">
          <p className="whitespace-pre-wrap break-words">
            {expanded || !needsReadMore ? ticket.description : `${shortDescription}...`}
          </p>
          {needsReadMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-primary-600 hover:text-primary-400 font-medium text-sm focus:outline-none flex items-center gap-1"
            >
              {expanded ? (
                <>
                  <i className="fa-light fa-chevron-left" />
                  Toon minder
                </>
              ) : (
                <>
                  Lees meer
                  <i className="fa-light fa-chevron-right" />
                </>
              )}
            </button>
          )}
        </div>
      )}

      {!hasDescription && (
        <p className="text-neutral-400 text-sm italic">Geen beschrijving beschikbaar</p>
      )}

      <div className="mt-4 pt-3 border-t border-neutral-200">
        <VoteButton
          ticket={ticket}
          userVoteInfo={userVoteInfo}
          onVoteChange={onVoteChange}
        />
      </div>

      <VoterList votes={ticket.votes} />
    </div>
  );
};

export default TicketCard;
